import memoizeOne from "memoize-one";
import {
  accountScopes,
  isMemberAddonRole,
  isProjectScopedScope,
  projectScopes,
  type MemberAddonRole,
  type ProjectScopedScope,
  type Scope,
  type UserRole,
  type VisitorScope,
} from "@cdx/shared/auth/scopes";
import {
  ACTION_SCOPES,
  type ActionKey,
  type ActionScopes,
  type IsProjectScoped,
} from "@cdx/shared/auth/action-scopes";
import type {Project, ProjectId} from "../../cdx-models/Project";
import type {Root} from "../../cdx-models/Root";
import type {User, UserId} from "../../cdx-models/User";
import type {UserProjectAccess} from "../../cdx-models/UserProjectAccess";
import {getUserRole} from "./roles";

/** `isLoaded: false` while the role, `staffPermission` or `addRoles` is still loading. */
type AuthContext =
  | {isLoaded: false}
  | {
      isLoaded: true;
      role: UserRole | null;
      userId: UserId | null;
      user: User | null;
      staffPermission: string;
      addRoles: readonly MemberAddonRole[];
    };

type LoadedContext = Extract<AuthContext, {isLoaded: true}>;

const NO_ADDON_ROLES: readonly MemberAddonRole[] = [];

const readAuthContext = (root: Root): AuthContext => {
  const {isLoaded, role, userId, user} = getUserRole(root);
  if (!isLoaded) return {isLoaded: false};
  // `accountScopes` ignores these two fields for every role but guest, so only guests load them.
  if (role !== "guest") {
    return {
      isLoaded: true,
      role,
      userId,
      user,
      staffPermission: "limited",
      addRoles: NO_ADDON_ROLES,
    };
  }
  const staffPermission = root.account.$meta.get("staffPermission", null);
  const roles = root.account.$meta.find("roles", {userId});
  const stored = roles.length ? roles[0].$meta.get("addRoles", null) : [];
  if (staffPermission === null || stored === null) return {isLoaded: false};
  return {
    isLoaded: true,
    role,
    userId,
    user,
    staffPermission,
    addRoles: stored.filter(isMemberAddonRole),
  };
};

// Memoized because `useCardBag` calls this for every rendered card.
let memo: {key: string; accountScopes: ReadonlySet<Scope>} | null = null;

const accountSetFor = (root: Root, ctx: LoadedContext): ReadonlySet<Scope> => {
  const {role, addRoles, staffPermission} = ctx;
  const accountId = root.account.$meta.get("id", null);
  const key = `${accountId}|${role}|${addRoles.join(",")}|${staffPermission}`;
  if (memo?.key !== key)
    memo = {key, accountScopes: accountScopes({role, addRoles, staffPermission})};
  return memo.accountScopes;
};

/**
 * Returns the account scopes the last check computed, or `null` before any check ran. Reads no
 * mate data, so the collector can call it while collecting. Not matched against the current
 * account, so right after an org switch it can still be the previous org's set.
 */
export const peekAccountScopes = (): ReadonlySet<Scope> | null => memo?.accountScopes ?? null;

// Mate's cache is cleared on login and logout, so the set computed before belongs to another caller.
export const forgetAccountScopes = () => {
  memo = null;
};

// Keyed on the array mate returns, which keeps its identity until the rows change.
const toProjectIds = memoizeOne((rows: UserProjectAccess[]): ReadonlySet<ProjectId> => {
  const ids = new Set<ProjectId>();
  for (const row of rows) {
    const projectId = row.$meta.getId("project", null);
    if (projectId) ids.add(projectId);
  }
  return ids;
});

/**
 * Returns the org's projects the caller produces, or `null` while loading. One query for the page
 * instead of one per project asked about.
 */
const getProducerProjectIds = (root: Root, user: User): ReadonlySet<ProjectId> | null => {
  const accountId = root.account.$meta.get("id", null);
  if (!accountId) return null;
  const query = {projectRole: "producer" as const, project: {accountId}};
  const rows = user.$meta.find("withProjectAccess", query, null);
  return rows ? toProjectIds(rows) : null;
};

export type HeldScopes =
  | {isLoaded: false; accountScopes: null; userId: null}
  | {isLoaded: true; accountScopes: ReadonlySet<Scope>; userId: UserId | null};

/** Returns the scopes the caller holds without naming a project. */
export const getHeldScopes = (root: Root): HeldScopes => {
  const ctx = readAuthContext(root);
  if (!ctx.isLoaded) return {isLoaded: false, accountScopes: null, userId: null};
  return {isLoaded: true, accountScopes: accountSetFor(root, ctx), userId: ctx.userId};
};

/** `orgWide` checks the account scopes only, as dispatch does for a global milestone or run config. */
export type ProjectTarget =
  | {project: Project; projectId?: never; orgWide?: never}
  | {projectId: ProjectId; project?: never; orgWide?: never}
  | {orgWide: true; project?: never; projectId?: never};

type NoProjectTarget = {project?: never; projectId?: never; orgWide?: never};

type AnyTarget = {project?: Project; projectId?: ProjectId; orgWide?: true};

// What an answer that depends on an unloaded row returns. `{fallback: null}` tells loading apart;
// `NoInfer` keeps a `boolean` annotation on the result from overriding it.
type Fallback = boolean | null;

type ScopeOpts<S extends Scope, F extends Fallback> = (S extends ProjectScopedScope
  ? ProjectTarget
  : NoProjectTarget) & {fallback?: F};

/** Makes `opts` optional only when none of its fields are required. */
type OptsArg<O> = object extends O ? [opts?: O] : [opts: O];

const warnedMissingTargets = new Set<string>();

// The type demands a target for a project-scoped scope, but a `.js` caller can leave it out, and
// then a guest producer is refused. Warned before the account set answers, so an admin sees it too.
const warnMissingTarget = (scope: Scope, target: AnyTarget) => {
  if (process.env.NODE_ENV === "production" || !isProjectScopedScope(scope)) return;
  if ("project" in target || "projectId" in target || target.orgWide) return;
  const stack = new Error().stack ?? "";
  if (warnedMissingTargets.has(stack)) return;
  warnedMissingTargets.add(stack);
  console.warn(
    `'${scope}' is project-scoped: pass {project}, {projectId} or {orgWide: true}`,
    stack
  );
};

const checkScope = <F extends Fallback>(
  root: Root,
  scope: Scope,
  target: AnyTarget,
  fallback: F
): boolean | F => {
  warnMissingTarget(scope, target);
  const ctx = readAuthContext(root);
  if (!ctx.isLoaded) return fallback;
  if (accountSetFor(root, ctx).has(scope)) return true;
  if (!isProjectScopedScope(scope) || target.orgWide) return false;
  // Admins returned above, and `projectScopes` gives no other role project scopes.
  if (ctx.role !== "guest" || !ctx.user) return false;
  const projectId = target.projectId ?? target.project?.$meta.get("id", null) ?? null;
  if (!projectId) return fallback;
  const producerIn = getProducerProjectIds(root, ctx.user);
  if (!producerIn) return fallback;
  return projectScopes({
    role: ctx.role,
    projectRole: producerIn.has(projectId) ? "producer" : "default",
    staffPermission: ctx.staffPermission,
  }).has(scope);
};

/**
 * A project-scoped scope requires a `project`, `projectId` or `orgWide: true`. A scope every
 * visitor holds is refused by the type: it cannot tell anyone apart.
 */
export const hasScope = <S extends Exclude<Scope, VisitorScope>, F extends Fallback = false>(
  root: Root,
  scope: S,
  ...[opts]: OptsArg<ScopeOpts<S, F>>
): boolean | NoInfer<F> => {
  const {fallback = false as F, ...target} = (opts ?? {}) as AnyTarget & {fallback?: F};
  return checkScope(root, scope, target, fallback);
};

type ProjectsTarget =
  | {projectIds: readonly ProjectId[]; projects?: never}
  | {projects: readonly Project[]; projectIds?: never};

/** Dispatch requires the scope in every project a call touches. An empty list checks org-wide. */
export const hasScopeInAll = <F extends Fallback = false>(
  root: Root,
  scope: ProjectScopedScope,
  opts: ProjectsTarget & {fallback?: F}
): boolean | NoInfer<F> => {
  const {fallback = false as F} = opts;
  const targets: AnyTarget[] = opts.projectIds
    ? opts.projectIds.map((projectId) => ({projectId}))
    : opts.projects.map((project) => ({project}));
  if (!targets.length) return checkScope(root, scope, {orgWide: true}, fallback);
  let isLoading = false;
  for (const target of targets) {
    const res = checkScope(root, scope, target, null);
    if (res === false) return false;
    if (res === null) isLoading = true;
  }
  return isLoading ? fallback : true;
};

/** For UI with no project picked yet, e.g. a create button. The server still checks per project. */
export const hasScopeInAny = <F extends Fallback = false>(
  root: Root,
  scope: ProjectScopedScope,
  {fallback = false as F}: {fallback?: F} = {}
): boolean | NoInfer<F> => {
  const ctx = readAuthContext(root);
  if (!ctx.isLoaded) return fallback;
  if (accountSetFor(root, ctx).has(scope)) return true;
  if (ctx.role !== "guest" || !ctx.user) return false;
  const producerIn = getProducerProjectIds(root, ctx.user);
  if (!producerIn) return fallback;
  // Producers hold every project-scoped scope.
  return producerIn.size > 0;
};

type Row<K extends ActionKey> = (typeof ACTION_SCOPES)[K];
type Declared<K extends ActionKey> = Row<K>["scopes"];

type TargetOpt<K extends ActionKey> =
  IsProjectScoped<Declared<K>> extends true ? ProjectTarget : NoProjectTarget;

type ScopesOpt<K extends ActionKey> =
  Row<K> extends {narrows: true}
    ? {scopes: Declared<K> extends readonly Scope[] ? readonly [...Declared<K>[number][]] : never}
    : {scopes?: never};

type MutationOpts<K extends ActionKey, F extends Fallback> = TargetOpt<K> &
  ScopesOpt<K> & {fallback?: F};

/**
 * Requires every scope the action declares. The type requires a project target for project-scoped
 * actions and `scopes` for actions marked `narrows`.
 */
export const hasScopeForMutation = <K extends ActionKey, F extends Fallback = false>(
  root: Root,
  key: K,
  ...[opts]: OptsArg<MutationOpts<K, F>>
): boolean | NoInfer<F> => {
  const {
    scopes,
    fallback = false as F,
    ...target
  } = (opts ?? {}) as AnyTarget & {scopes?: readonly Scope[]; fallback?: F};
  const declared: ActionScopes = ACTION_SCOPES[key].scopes;
  if (declared === "public") return true;
  if (declared === "session") {
    const ctx = readAuthContext(root);
    if (!ctx.isLoaded) return fallback;
    return ctx.userId !== null;
  }
  let isLoading = false;
  for (const scope of scopes ?? declared) {
    const res = checkScope(root, scope, target, null);
    if (res === false) return false;
    if (res === null) isLoading = true;
  }
  return isLoading ? fallback : true;
};
